import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Card, Form, Badge, Modal, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

const fmtDate = (d) =>
  new Date(d).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit', timeZone: 'UTC' });

const MealPlanManager = () => {
  const [plans, setPlans] = useState([]);
  const [viewing, setViewing] = useState(null);

  // Assistant chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [chatError, setChatError] = useState('');
  const chatEndRef = useRef(null);

  const fetchPlans = async () => {
    try {
      const { data } = await api.get('/meal-plans/admin');
      setPlans(data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  const sendMessage = async (e) => {
    e.preventDefault();
    const content = input.trim();
    if (!content || thinking) return;
    const next = [...messages, { role: 'user', content }];
    setMessages(next);
    setInput('');
    setThinking(true);
    setChatError('');
    try {
      const { data } = await api.post(
        '/meal-plans/assistant',
        { messages: next },
        { timeout: 270000 }
      );
      if (data.type === 'saved') {
        setMessages([
          ...next,
          {
            role: 'assistant',
            content: `${data.text}\n\nSaved to the site (${data.recipeCount} recipes). It's now live on the What Am I Eating page.`,
          },
        ]);
        fetchPlans();
      } else {
        setMessages([...next, { role: 'assistant', content: data.text }]);
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      setChatError(msg);
      // Roll the failed turn back out so Mike can resend it.
      setMessages(messages);
      setInput(content);
    } finally {
      setThinking(false);
    }
  };

  const startPlanning = () => {
    setMessages([]);
    setChatError('');
    setInput('Plan dinners for next week.');
  };

  const toggleStatus = async (plan) => {
    try {
      await api.put(`/meal-plans/${plan._id}`, {
        status: plan.status === 'active' ? 'archived' : 'active',
      });
      fetchPlans();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this meal plan? (Recipes stay in the library.)')) {
      try {
        await api.delete(`/meal-plans/${id}`);
        fetchPlans();
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <div>
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Plan a Week with Jim</strong>
          <Button variant="outline-secondary" size="sm" onClick={startPlanning}>
            New Conversation
          </Button>
        </Card.Header>
        <Card.Body>
          <div
            style={{ maxHeight: '420px', overflowY: 'auto' }}
            className="mb-3"
          >
            {messages.length === 0 && (
              <p className="text-muted mb-0">
                Ask Jim to plan the week. He pulls the family calendars (Jax custody, Morgan's
                shifts, baseball), proposes a menu, and iterates with you. When you approve, the
                plan and recipes save to the site automatically. Turns that pull calendars can
                take a minute or two.
              </p>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`p-2 px-3 mb-2 rounded ${m.role === 'user' ? 'bg-primary text-white ms-5' : 'bg-light me-5 border'}`}
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {m.content}
              </div>
            ))}
            {thinking && (
              <div className="text-muted small">
                <Spinner animation="border" size="sm" className="me-2" />
                Jim is working (calendar pulls can take a couple minutes)...
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          {chatError && <Alert variant="danger">{chatError}</Alert>}
          <Form onSubmit={sendMessage} className="d-flex gap-2">
            <Form.Control
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. Plan dinners for next week"
              disabled={thinking}
            />
            <Button type="submit" disabled={thinking || !input.trim()}>
              Send
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h4 className="mb-0">Saved Plans</h4>
        <Link to="/eating" target="_blank" className="small">
          View public page
        </Link>
      </div>
      <Table striped hover responsive>
        <thead>
          <tr>
            <th>Plan</th>
            <th>Dates</th>
            <th>Dinners</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {plans.map((p) => (
            <tr key={p._id}>
              <td>{p.title}</td>
              <td className="text-nowrap">{fmtDate(p.startDate)} - {fmtDate(p.endDate)}</td>
              <td>{p.days.filter((d) => !d.skip).length}</td>
              <td>
                <Badge bg={p.status === 'active' ? 'success' : 'secondary'}>{p.status}</Badge>
              </td>
              <td className="text-end text-nowrap">
                <Button size="sm" variant="outline-primary" className="me-2" onClick={() => setViewing(p)}>
                  View
                </Button>
                <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => toggleStatus(p)}>
                  {p.status === 'active' ? 'Archive' : 'Activate'}
                </Button>
                <Button size="sm" variant="outline-danger" onClick={() => handleDelete(p._id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
          {plans.length === 0 && (
            <tr>
              <td colSpan="5" className="text-muted text-center">No meal plans yet.</td>
            </tr>
          )}
        </tbody>
      </Table>

      <Modal show={!!viewing} onHide={() => setViewing(null)} size="lg">
        {viewing && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{viewing.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {viewing.summary && <p className="text-muted">{viewing.summary}</p>}
              <Table size="sm" striped>
                <thead>
                  <tr><th>Date</th><th>Dinner</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  {viewing.days.map((d, i) => (
                    <tr key={i}>
                      <td className="text-nowrap">{fmtDate(d.date)}</td>
                      <td>{d.skip ? <em>Night off</em> : (d.recipe?.title || d.dinnerTitle)}</td>
                      <td className="small text-muted">{d.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {(viewing.shoppingList || []).length > 0 && (
                <>
                  <h6 className="mt-3">Shopping List</h6>
                  {viewing.shoppingList.map((sec, i) => (
                    <div key={i} className="mb-2">
                      <strong className="small text-uppercase">{sec.section}</strong>
                      <ul className="mb-1 small">
                        {sec.items.map((it, j) => (
                          <li key={j}>
                            {it.text}{it.qty ? ` (${it.qty})` : ''}{it.for ? ` [${it.for}]` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </>
              )}
            </Modal.Body>
          </>
        )}
      </Modal>
    </div>
  );
};

export default MealPlanManager;
